import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { ContentDivider } from '@/components/application/content-divider/content-divider';
import { Button, LinkButton } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { ExternalLink } from '@/components/base/link/link';
import { notify } from '@/components/base/notifications/notifications';
import {
  RecaptchaScript,
  RecaptchaV2Challenge,
  useRecaptcha,
} from '@/hooks/use-recaptcha';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { useSignupStartedConversion } from '@/lib/analytics/use-signup-started-conversion';
import { syncableProfileCookieSettings } from '@/lib/consent/consent-profile';
import { useConsent } from '@/lib/consent/use-consent';
import { AccountExistsNotice } from '@/routes/-auth/account-exists-notice';
import {
  readUtmHiddenField,
  UtmHiddenField,
} from '@/routes/-auth/utm-hidden-field';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { pickUtmValues, toInitialUtm } from '@/schemas/utm';
import { ERROR_CODES, signupCompletedDedupId } from '@common/utils';

import {
  researcherSignupSearchSchema,
  useSignupMutation,
} from './-auth/mutation';
import { ResearcherRightPanel } from './-auth/right-panel';
import { RESEARCHER_SIGNUP_METHODS, SocialLogins } from './-auth/social-logins';

export const Route = createFileRoute('/researchers/signup')({
  validateSearch: researcherSignupSearchSchema,
  beforeLoad: async ({ context }) => {
    // Root-context session, not an independent `getServerSession()` — see the
    // note in `researchers/login.tsx` for why the two must not diverge.
    if (context.user) {
      throw redirect({ to: '/researchers' });
    }
  },
  component: ResearcherSignupPage,
});

function ResearcherSignupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const analytics = useAnalytics();
  // Resolves the ad-consent grants the server-side `signup_completed` answers
  // to. Researchers have no profile for it to be persisted onto, so this is
  // the only route by which core-api learns the decision.
  const { stored: storedConsent } = useConsent();
  const signupCookieSettings = syncableProfileCookieSettings(storedConsent);
  const [v2Token, setV2Token] = useState<string | null>(null);
  const { getToken } = useRecaptcha();
  const recaptchaResetRef = useRef<(() => void) | null>(null);

  const referralCode = search.referralCode ?? search.r;

  // Every way into an account this page offers, so the reported method is the
  // page's own shape and not a guess at which one this visitor will take.
  useSignupStartedConversion({
    methods: RESEARCHER_SIGNUP_METHODS,
    attributes: {
      referralCode: referralCode ?? null,
      utmSource: search.utm_source ?? null,
    },
  });

  const signup = useSignupMutation();
  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
    validators: { onSubmit: signup.schema },
    onSubmit: async ({ value }) => {
      const captchaToken = v2Token ? 'v2-fallback' : await getToken('signup');
      // This page's own URL first; the tag container's carried-over values
      // fill the fields it has nothing for. Resolved here rather than at
      // render because the container fills its field after the page mounts.
      const initialUtm = toInitialUtm({
        ...readUtmHiddenField('researcher'),
        ...pickUtmValues(search),
      });
      signup.mutation.mutate(
        {
          fields: value,
          captchaToken,
          initialUtm,
          referralCode,
          ...(signupCookieSettings && {
            cookieSettings: signupCookieSettings,
          }),
          ...(v2Token && { captchaV2Token: v2Token }),
        },
        {
          onSuccess: (created) => {
            // The dedup id is derived from the account this request created, so
            // this and core-api's report of the same signup collapse into one
            // conversion.
            //
            // `immediate` because the navigation below reloads the document:
            // a batched conversion would be lost with the page.
            analytics?.conversion(
              {
                name: 'signup_completed',
                eventId: signupCompletedDedupId(created.user.id),
                attributes: {
                  method: 'email',
                  hasReferral: !!referralCode,
                },
              },
              { immediate: true },
            );
            void navigate({
              to: '/researchers/verify-email',
              reloadDocument: true,
            });
          },
          onError: (error) => {
            // `toSignupError` only keeps this code when a URL came with it,
            // so the guard is for the type rather than for a real case.
            if (
              error.code === ERROR_CODES.SSO_REDIRECT_REQUIRED &&
              error.redirect
            ) {
              globalThis.location.href = error.redirect;
            } else if (error.code === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED) {
              // handled in render path
            } else if (error.code === ERROR_CODES.ACCOUNT_EXISTS) {
              if (v2Token) {
                setV2Token(null);
                recaptchaResetRef.current?.();
              }
              analytics?.ui({
                action: 'viewed',
                actionSubject: 'signupAccountExistsNotice',
                actionSubjectId: 'researcher',
                source: 'signupPage',
              });
            } else {
              notify({
                title: t`Signup failed`,
                description: error.message,
                color: 'error',
                onDismiss: () => signup.mutation.reset(),
              });
            }
          },
        },
      );
    },
  });

  const showV2Challenge =
    signup.mutation.error?.code === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED;

  // Derived from the attempt that failed, like `showV2Challenge` above, rather
  // than held in state: the address the conflict was about is the one that was
  // submitted.
  const conflictedEmail =
    signup.mutation.error?.code === ERROR_CODES.ACCOUNT_EXISTS
      ? signup.mutation.variables?.fields.email.trim()
      : undefined;

  return (
    <AuthLayout rightPanel={<ResearcherRightPanel />}>
      <RecaptchaScript />
      <form
        // disable native validation in favour of Zod schema.
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <UtmHiddenField side="researcher" />

        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Create a researcher account</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              Publish your project and recruit from Respondent&apos;s network of
              verified professionals.
            </Trans>
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-5 sm:flex-row sm:gap-2.5">
              <FormInput
                type="text"
                form={form}
                field="firstName"
                label={t`First name`}
                placeholder={t`Jane`}
                isRequired
                size="md"
              />
              <FormInput
                type="text"
                form={form}
                field="lastName"
                label={t`Last name`}
                placeholder={t`Doe`}
                isRequired
                size="md"
              />
            </div>

            <FormInput
              type="email"
              form={form}
              field="email"
              label={t`Work email`}
              placeholder={t`name@company.com`}
              isRequired
              size="md"
            />

            <div className="flex flex-col gap-5 sm:flex-row sm:gap-2.5">
              <FormInput
                type="password"
                form={form}
                field="password"
                label={t`Password`}
                placeholder={t`8+ character minimum`}
                hint={t`Must be at least 8 characters.`}
                isRequired
                size="md"
              />
              <FormInput
                type="password"
                form={form}
                field="confirmPassword"
                label={t`Confirm password`}
                placeholder={t`Re-enter password`}
                isRequired
                size="md"
              />
            </div>

            <FormInput
              form={form}
              field="termsAccepted"
              type="checkbox"
              size="md"
              isRequired
              label={
                <span className="text-sm text-inherit">
                  <Trans>
                    I agree to Respondent&apos;s{' '}
                    <ExternalLink
                      href="https://www.respondent.io/terms-conditions"
                      target="_blank"
                      className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
                    >
                      Terms &amp; Conditions
                    </ExternalLink>{' '}
                    and{' '}
                    <ExternalLink
                      href="https://www.respondent.io/privacy-policy"
                      target="_blank"
                      className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
                    >
                      Privacy Policy
                    </ExternalLink>
                  </Trans>
                </span>
              }
            />
          </div>

          {showV2Challenge ? (
            <RecaptchaV2Challenge
              onVerify={(token) => setV2Token(token)}
              onExpire={() => setV2Token(null)}
              resetRef={recaptchaResetRef}
            />
          ) : conflictedEmail ? (
            <AccountExistsNotice email={conflictedEmail} />
          ) : signup.mutation.error ? (
            <AlertFloating
              color="error"
              title="Signup failed"
              description={signup.mutation.error.message}
              confirmLabel=""
            />
          ) : null}

          <Button
            type="submit"
            color="primary"
            size="lg"
            isLoading={signup.mutation.isPending}
            className="w-full"
          >
            <Trans>Create an account</Trans>
          </Button>

          <ContentDivider type="single-line">
            <Trans>or</Trans>
          </ContentDivider>

          <SocialLogins referralCode={referralCode} />
        </div>

        <ul className="flex flex-col items-start gap-1">
          <li className="text-sm text-tertiary">
            <Trans>Already have a researcher account?</Trans>{' '}
            <LinkButton color="link-color" size="md" to="/researchers/login">
              <Trans>Log in</Trans>
            </LinkButton>
          </li>

          <li className="text-sm text-tertiary">
            <Trans>Not a researcher?</Trans>{' '}
            <LinkButton color="link-color" size="md" to="/participants/signup">
              <Trans>Sign up as a participant</Trans>
            </LinkButton>
          </li>
        </ul>
      </form>
    </AuthLayout>
  );
}
